/**
 * El Markdown de las respuestas del asistente, con tres cierres que antes no
 * tenía (auditoría 23-09-2026):
 *
 *  - SIN IMÁGENES. react-markdown pinta `![x](https://…)` por defecto, y una
 *    imagen es una petición a un servidor ajeno con lo que se meta en la URL:
 *    una tarea con un título trampa podía convertir la respuesta en un canal
 *    para sacar datos. Se quitan (`disallowedElements`).
 *  - ENLACES CON LISTA BLANCA. Un enlace interno solo si es una PÁGINA del
 *    registro de acciones (src/lib/assistantActions.ts) a secas —sin
 *    parámetros ni ids: `isAllowedMarkdownUrl`—, y navega por el router
 *    cerrando el panel; uno externo solo a la web, las tiendas, el soporte y las
 *    fuentes oficiales. Cualquier otro se pinta como texto. Lo concreto («la
 *    ficha de Amadou en Acceso») llega SOLO como botón construido por el servidor
 *    (open_in_app), que filtra por rol: el modelo tiene ids de otras
 *    herramientas y un enlace suyo con `?edit=<uuid>` se saltaba ese filtro.
 *  - TABLAS (remark-gfm). El modelo ya contestaba comparativas en tabla y sin
 *    el plugin salían como líneas de barras verticales.
 */
import { useContext, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { AssistantContext } from "@/contexts/AssistantContext";
import { isAllowedExternalUrl, isAllowedMarkdownUrl } from "@/lib/assistantActions";

/** Deja pasar solo URLs de la lista blanca; el resto se vacía (y se pinta como texto). */
export function assistantUrlTransform(url: string): string {
  if (isAllowedMarkdownUrl(url) || isAllowedExternalUrl(url)) return url;
  return "";
}

function AssistantLink({ href, children }: { href?: string; children?: ReactNode }) {
  const navigate = useNavigate();
  const assistant = useContext(AssistantContext);
  if (href && isAllowedMarkdownUrl(href)) {
    return (
      <a
        href={href}
        onClick={(e) => {
          // Por dentro de la app: un href normal recargaría la SPA entera.
          e.preventDefault();
          assistant?.setIsPanelOpen(false);
          navigate(href);
        }}
        className="font-semibold text-[#2E84DE] underline underline-offset-2"
      >
        {children}
      </a>
    );
  }
  if (href && isAllowedExternalUrl(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#2E84DE] underline underline-offset-2">
        {children}
      </a>
    );
  }
  return <span>{children}</span>;
}

const COMPONENTS: Components = {
  a: ({ href, children }) => <AssistantLink href={href}>{children}</AssistantLink>,
  /* Una tabla ancha no puede empujar la burbuja fuera de la pantalla del
     teléfono: se desplaza ella sola en horizontal. */
  table: ({ children }) => (
    <div className="my-2 -mx-1 overflow-x-auto">
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border-b border-border/60 px-2 py-1 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="border-b border-border/30 px-2 py-1 align-top">{children}</td>,
};

const DISALLOWED = ["img"];
const PLUGINS = [remarkGfm];

export function AssistantMarkdown({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={PLUGINS}
      disallowedElements={DISALLOWED}
      urlTransform={assistantUrlTransform}
      components={COMPONENTS}
    >
      {content}
    </ReactMarkdown>
  );
}
